import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Trophy, Users, Calendar, Loader2, AlertCircle, ChevronLeft, ChevronRight, Clock, Star, Award, Crown, Sparkles, ArrowRight } from 'lucide-react';
import { differenceInSeconds } from 'date-fns';
import { cn } from '../../lib/utils';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { LazyImage } from '../../components/shared/LazyImage';
import { PublicLayout } from '../../components/layout/PublicLayout';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';
import { supabase } from '../../lib/supabase';
import { TopFiveGlobal } from '../../components/shared/TopFiveGlobal'; // Importer le composant

// Type pour les concours à venir
type UpcomingContest = {
  id: string;
  name: string;
  description: string;
  start_date: string;
  end_date: string;
  status: 'draft' | 'active' | 'completed';
  vote_price: number;
  contestants_count?: number;
  votes_count?: number;
  slug: string;
  photo_url?: string;
  logo_url?: string;
  is_public: boolean;
  code_unique: string;
}

// Fonction pour formater une date en français
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const datePart = date.toLocaleDateString('fr-FR', { 
    day: 'numeric', 
    month: 'short', 
    year: 'numeric'
  });
  const timePart = date.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit'
  });
  
  return `${datePart} à ${timePart}`;
};

// Composant pour le chronomètre
const CountdownTimer = ({ targetDate, type }: { targetDate: string, type: 'start' | 'end' }) => {
  const [timeLeft, setTimeLeft] = useState<{ days: number; hours: number; minutes: number; seconds: number }>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  
  React.useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const target = new Date(targetDate);
      const totalSeconds = Math.max(0, differenceInSeconds(target, now));
      
      const days = Math.floor(totalSeconds / (60 * 60 * 24));
      const hours = Math.floor((totalSeconds % (60 * 60 * 24)) / (60 * 60));
      const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
      const seconds = Math.floor(totalSeconds % 60);
      
      setTimeLeft({ days, hours, minutes, seconds });
    };
    
    // Calculer immédiatement
    calculateTimeLeft();
    
    // Mettre à jour chaque seconde
    const timer = setInterval(calculateTimeLeft, 1000);
    
    return () => clearInterval(timer);
  }, [targetDate]);
  
  const isExpired = timeLeft.days === 0 && timeLeft.hours === 0 && timeLeft.minutes === 0 && timeLeft.seconds === 0;
  
  if (isExpired) {
    return (
      <div className="text-xs text-white">
        {type === 'start' ? 'Démarrage imminent' : 'Terminé'}
      </div>
    );
  }
  
  return (
    <div className="flex flex-col items-center">
      <div className="flex space-x-1 text-xs font-medium">
        {timeLeft.days > 0 && (
          <span className="text-white">{timeLeft.days}j</span>
        )}
        <span className="text-white">
          {String(timeLeft.hours).padStart(2, '0')}:
          {String(timeLeft.minutes).padStart(2, '0')}:
          {String(timeLeft.seconds).padStart(2, '0')}
        </span>
      </div>
      <span className="text-yellow-400 text-xs">
        {type === 'start' ? 'avant début' : 'restant'}
      </span>
    </div>
  );
};

export const UpcomingContests = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;

  const { data: upcomingContests, isLoading, error } = useQuery({
    queryKey: ['upcomingContests'],
    queryFn: async () => {
      // Récupérer les concours à venir (draft) qui sont publics
      let query = supabase
        .from('contests')
        .select(`
          id,
          name,
          description,
          start_date,
          end_date,
          status,
          vote_price,
          slug,
          photo_url,
          logo_url,
          is_public,
          code_unique
        `)
        .eq('status', 'draft')
        .eq('is_public', true);

      const { data: contestsData, error: contestsError } = await query;
      if (contestsError) throw contestsError;

      // Pour chaque concours, récupérer le nombre de candidats
      const contestsWithCounts = await Promise.all(
        contestsData.map(async (contest) => {
          // Compter les candidats
          const { count: contestantsCount } = await supabase
            .from('contestants')
            .select('*', { count: 'exact', head: true })
            .eq('contest_id', contest.id);

          return {
            ...contest,
            contestants_count: contestantsCount || 0,
          } as UpcomingContest;
        })
      );

      return contestsWithCounts;
    }
  });

  // Pagination logic
  const totalPages = upcomingContests ? Math.ceil(upcomingContests.length / itemsPerPage) : 0;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentContests = upcomingContests ? upcomingContests.slice(startIndex, endIndex) : [];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <PublicLayout>
      <SEOMetaTags
        title="Concours à Venir | ERNEST NZOGOUG"
        description="Découvrez les concours à venir sur ERNEST NZOGOUG. Inscrivez-vous et participez pour gagner des prix exceptionnels et obtenir de la visibilité."
        image="https://nzofoug.com/contests-showcase.jpg"
        url="https://nzofoug.com/concours"
      />
      
      <div className="bg-[#111216] text-white relative overflow-hidden pt-20">
        {/* Background Image avec Overlay et Parallaxe */}
        <div 
          className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
          style={{
            backgroundImage: 'url(https://images.rawpixel.com/image_800/cHJpdmF0ZS9sci9pbWFnZXMvd2Vic2l0ZS8yMDI0LTA4L3N0YXJ0dXBpbWFnZXNfZ29sZF9nbGl0dGVyX2dvbGRfY29uZmV0dGlfYm9rZWhfc3BhcmtsZV9zaW1wbGVfZ185MTEzYzJkMy03YjEyLTQwYmQtYjNhYS04OTVlOGU4MmM5N2NfMi5qcGc.jpg)',
            transform: 'translateZ(0)',
            willChange: 'transform',
            backgroundAttachment: 'fixed'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/95 via-black/80 to-black/95 backdrop-blur-[2px]"></div>
        </div>

        <div className="relative z-10 container mx-auto px-4 pb-20">
          {/* Hero Section */}
          <div className="max-w-7xl mx-auto mb-16">
            <div className="text-center relative">
              {/* Éléments décoratifs */}
              <div className="absolute top-0 left-1/4 w-2 h-2 sparkle" style={{ animationDelay: '0s' }}></div>
              <div className="absolute top-10 right-1/3 w-3 h-3 sparkle" style={{ animationDelay: '0.5s' }}></div>
              <div className="absolute bottom-0 right-1/4 w-2 h-2 sparkle" style={{ animationDelay: '1s' }}></div>
              
              <div className="flex justify-center mb-8">
                <div className="relative floating">
                  <div className="absolute -inset-8 bg-gradient-to-r from-yellow-600 via-yellow-400 to-yellow-600 rounded-full blur-xl opacity-30"></div>
                  <Trophy className="w-20 h-20 text-yellow-400 relative" />
                </div>
              </div>
              
              <h1 className="text-5xl sm:text-6xl font-extrabold mb-6 leading-tight">
                <span className="bg-gradient-to-r from-yellow-200 via-yellow-400 to-yellow-200 text-transparent bg-clip-text">
                  Prochains Concours
                </span>
              </h1>
              
              <p className="text-xl sm:text-2xl text-gray-300 max-w-3xl mx-auto mb-8 leading-relaxed">
                Saisissez l'opportunité de briller et de vous faire remarquer dans les concours à venir. 
                <span className="text-yellow-400"> Inscrivez-vous dès maintenant!</span>
              </p>
              
              {/* Statistiques */}
              <div className="flex flex-wrap justify-center gap-8 mb-12">
                <div className="flex flex-col items-center bg-black/30 backdrop-blur-sm border border-white/10 rounded-xl px-8 py-4">
                  <Users className="w-8 h-8 text-yellow-400 mb-2" />
                  <span className="text-2xl font-bold text-white">1000+</span>
                  <span className="text-yellow-400">Participants</span>
                </div>
                
                <div className="flex flex-col items-center bg-black/30 backdrop-blur-sm border border-white/10 rounded-xl px-8 py-4">
                  <Trophy className="w-8 h-8 text-yellow-400 mb-2" />
                  <span className="text-2xl font-bold text-white">50+</span>
                  <span className="text-yellow-400">Concours</span>
                </div>
                
                <div className="flex flex-col items-center bg-black/30 backdrop-blur-sm border border-white/10 rounded-xl px-8 py-4">
                  <Award className="w-8 h-8 text-yellow-400 mb-2" />
                  <span className="text-2xl font-bold text-white">200+</span>
                  <span className="text-yellow-400">Prix</span>
                </div>
              </div>
              
              {/* Appel à l'action */}
              <div className="max-w-2xl mx-auto bg-black/50 backdrop-blur-md rounded-xl p-6 border border-yellow-400/30">
                <h2 className="text-2xl font-bold text-white mb-3">Pourquoi participer?</h2>
                <p className="text-gray-300 mb-4">
                  Participez aux concours pour gagner des prix exceptionnels, obtenir de la visibilité et faire partie d'une communauté dynamique de talents africains.
                </p>
                <div className="flex justify-center">
                  <a href="#concours" className="inline-flex items-center gap-2 bg-yellow-400 hover:bg-yellow-500 text-black font-bold py-3 px-6 rounded-lg transition-colors duration-300">
                    Voir les concours <ArrowRight className="w-5 h-5" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Section principale avec les concours */}
          <div id="concours" className="max-w-7xl mx-auto backdrop-blur-md bg-black/30 rounded-3xl border border-white/10 shadow-2xl p-8 sm:p-12">
            {/* En-tête de section */}
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <div className="bg-yellow-400/20 p-2 rounded-full">
                  <Calendar className="w-6 h-6 text-yellow-400" />
                </div>
                <h2 className="text-2xl font-bold text-white">Concours à venir</h2>
              </div>
              
              <div className="hidden md:flex items-center gap-2 text-sm text-gray-400">
                <Sparkles className="w-4 h-4 text-yellow-400" />
                <span>Inscrivez-vous pour participer</span>
              </div>
            </div>

            {/* Liste des concours */}
            {isLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-yellow-400" />
              </div>
            ) : error ? (
              <div className="flex justify-center items-center h-64 text-destructive">
                <AlertCircle className="h-5 w-5 mr-2" />
                <span>Une erreur est survenue lors du chargement des concours</span>
              </div>
            ) : !upcomingContests?.length ? (
              <div className="text-center py-12">
                <Trophy className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-300 mb-2">
                  Aucun concours à venir trouvé
                </h3>
                <p className="text-gray-400">
                  Aucun concours à venir n'est disponible pour le moment
                </p>
              </div>
            ) : (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {currentContests.map((contest) => (
                    <div
                      key={contest.id}
                      className="group relative overflow-hidden rounded-xl transition-all duration-300 hover:translate-y-[-4px] hover:shadow-lg hover:shadow-yellow-400/20"
                    >
                      {/* Carte principale */}
                      <div className="relative bg-gradient-to-b from-gray-900/90 to-black border border-white/10 rounded-xl overflow-hidden">
                        {/* Image de couverture avec overlay */}
                        <div className="relative h-48 overflow-hidden">
                          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent z-10"></div>
                          <LazyImage
                            src={contest.logo_url || contest.photo_url || `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/candidates/${contest.id}`}
                            alt={contest.name}
                            className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500 ease-out"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?q=80&w=1470&auto=format&fit=crop';
                            }}
                          />
                          
                          {/* Effet de brillance sur l'image au survol */}
                          <div className="absolute inset-0 bg-gradient-to-tr from-yellow-400/0 to-yellow-400/0 group-hover:from-yellow-400/10 group-hover:to-yellow-400/0 transition-all duration-500 z-10"></div>
                        </div>

                        {/* Contenu */}
                        <div className="relative px-5 py-4">
                          {/* Badge de statut */}
                          <div className="absolute top-[-12px] right-5 z-20">
                            <Badge 
                              className="px-3 py-0.5 text-xs font-medium rounded-full bg-gradient-to-r from-orange-500 to-yellow-500 border-none text-white"
                            >
                              À venir
                            </Badge>
                          </div>

                          {/* Titre et Description */}
                          <div className="mb-4">
                            <h3 className="text-lg font-semibold mb-1 text-white group-hover:text-yellow-400 transition-colors duration-300 line-clamp-1">
                              {contest.name}
                            </h3>
                            <p className="text-gray-400 text-sm line-clamp-2">
                              {contest.description}
                            </p>
                          </div>

                          {/* Informations */}
                          <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                            <div className="flex items-center gap-2">
                              <Users className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                              <span className="text-gray-300">
                                <span className="font-medium text-white">{contest.contestants_count || 0}</span> participants
                              </span>
                            </div>
                            
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                              <CountdownTimer targetDate={contest.start_date} type="start" />
                            </div>
                            
                            <div className="flex items-center gap-2 col-span-2">
                              <Calendar className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                              <span className="text-gray-300 text-xs">
                                Début: <span className="text-white">{formatDate(contest.start_date)}</span>
                              </span>
                            </div>
                          </div>

                          {/* Boutons */}
                          <div className="flex gap-2">
                            <Link to={`/inscription/${contest.code_unique}`} className="flex-1">
                              <button className="w-full py-2 bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 rounded-md text-black font-medium text-sm transition-colors duration-300">
                                S'inscrire
                              </button>
                            </Link>
                            <Link to={`/c/${contest.slug}`} className="flex-1">
                              <button className="w-full py-2 bg-transparent hover:bg-white/10 border border-yellow-400 rounded-md text-yellow-400 font-medium text-sm transition-colors duration-300">
                                Détails
                              </button>
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {upcomingContests && upcomingContests.length > 0 && (
                  <div className="mt-12 flex justify-center items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="border-yellow-400/20 hover:bg-yellow-400/10 hover:text-yellow-400 rounded-full w-10 h-10 p-0"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    
                    {Array.from({ length: Math.max(totalPages, 1) }, (_, i) => i + 1).map((page) => (
                      <Button
                        key={page}
                        variant={currentPage === page ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(page)}
                        className={cn(
                          "rounded-full w-10 h-10 p-0 font-bold",
                          currentPage === page 
                            ? "bg-yellow-400 hover:bg-yellow-500 text-black" 
                            : "border-yellow-400/20 hover:bg-yellow-400/10 hover:text-yellow-400"
                        )}
                      >
                        {page}
                      </Button>
                    ))}
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages || totalPages === 0}
                      className="border-yellow-400/20 hover:bg-yellow-400/10 hover:text-yellow-400 rounded-full w-10 h-10 p-0"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Section FAQ */}
          <div className="max-w-4xl mx-auto mt-20 bg-black/30 backdrop-blur-md rounded-3xl border border-white/10 shadow-2xl p-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-white mb-4">Questions fréquentes</h2>
              <p className="text-gray-300">Tout ce que vous devez savoir pour participer aux concours</p>
            </div>
            
            <div className="space-y-6">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
                <h3 className="text-xl font-bold text-yellow-400 mb-2">Comment participer à un concours?</h3>
                <p className="text-gray-300">
                  Pour participer, il vous suffit de cliquer sur le bouton "S'inscrire" du concours qui vous intéresse, 
                  puis de remplir le formulaire d'inscription avec vos informations et votre candidature.
                </p>
              </div>
              
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
                <h3 className="text-xl font-bold text-yellow-400 mb-2">Quels sont les critères de sélection?</h3>
                <p className="text-gray-300">
                  Chaque concours a ses propres critères de sélection. Généralement, nous recherchons l'originalité, 
                  la qualité, la créativité et le respect des règles du concours.
                </p>
              </div>
              
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
                <h3 className="text-xl font-bold text-yellow-400 mb-2">Quels sont les prix à gagner?</h3>
                <p className="text-gray-300">
                  Les prix varient selon les concours. Ils peuvent inclure des récompenses financières, 
                  de la visibilité médiatique, des opportunités professionnelles et bien plus encore.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Top 5 Global */}
      <div className="mt-16">
        <div className="bg-white/5 backdrop-blur-sm rounded-lg border border-white/10 p-6">
          <TopFiveGlobal />
        </div>
      </div>
    </PublicLayout>
  );
};
